/**
 * BulkCaptureButton
 * Multi-file camera/gallery capture for inspection evidence.
 */
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Loader2 } from 'lucide-react';
import { useUploadInspectionEvidence } from '@/hooks/useInspectionEvidence';
import { toast } from '@/hooks/use-toast';

interface BulkCaptureButtonProps {
  inspectionId: string;
  ticketId: string;
  shopId: string;
}

export function BulkCaptureButton({ inspectionId, ticketId, shopId }: BulkCaptureButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const uploadMut = useUploadInspectionEvidence();

  const handleFiles = async (files: FileList) => {
    const fileArr = Array.from(files);
    if (!fileArr.length) return;
    setUploading(fileArr.length);
    let success = 0;
    for (const file of fileArr) {
      try {
        await uploadMut.mutateAsync({ inspectionId, ticketId, shopId, file });
        success++;
      } catch (err) {
        console.error('Upload failed:', err);
      }
      setUploading(prev => prev - 1);
    }
    setUploading(0);
    if (success > 0) {
      toast({ title: `${success} file${success > 1 ? 's' : ''} uploaded` });
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={e => e.target.files && handleFiles(e.target.files)}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={uploading > 0}
        className="h-8"
      >
        {uploading > 0 ? (
          <><Loader2 className="h-3 w-3 mr-1 animate-spin" />{uploading} uploading...</>
        ) : (
          <><Camera className="h-3 w-3 mr-1" />Capture</>
        )}
      </Button>
    </>
  );
}
